import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Can } from '@casl/react';
import { Add, PermContactCalendar } from '@mui/icons-material';
import {
  Box,
  Checkbox,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';

import { UserRoles } from '@bandido/shared/types';
import { AgentStatus } from '@bandido/shared/constants';

import { useTwilio } from '@frontend/features/communications/TwilioContextProvider/provider';
import { Link } from '@frontend/features/common/components/Link';
import { ListLayout } from '@frontend/features/common/components/ListLayout';
import { TimeAgo } from '@frontend/features/common/components/TimeAgo';
import { QueryKeys } from '@frontend/features/common/types';
import { Table, usePagination } from '@frontend/features/common/components/Table';
import { SearchInput } from '@frontend/features/common/components/SearchInput/SearchInput';

import { useGetAllUsers } from '@frontend/features/users/queries';
import UserStatusMenu from '@frontend/features/users/components/UserStatusMenu';
import { getAgentStatusColor } from '@frontend/features/users/helpers';

import { CreateUserWithClient } from '@frontend/features/auth/components/CreateUserWithClient';
import { useAuth } from '@frontend/features/auth/hooks/useAuth';

import { routes } from '../routes';

const rolesOptions = [UserRoles.ADMINS, UserRoles.OPERATORS, UserRoles.AGENTS];

export const UsersList = () => {
  const theme = useTheme();
  const { ability } = useAuth();
  const { t } = useTranslation();
  const { connectedAgents } = useTwilio();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUserId, setSelectedUserId] = useState<string>();
  const [selectedRoles, setSelectedRoles] = useState<UserRoles[]>([]);
  const [showCreateUser, setShowCreateUser] = useState(false);

  const toggleShowCreateUser = () => setShowCreateUser((s) => !s);

  const pagination = usePagination();
  const [search, setSearch] = useState('');

  const { data: users, isLoading } = useGetAllUsers({
    pageSize: pagination.rowsPerPage,
    currentPage: pagination.page,
    search: search,
    roles: selectedRoles.join(','),
  });

  const usersWithStatus = useMemo(
    () =>
      users?.items?.map((agent) => {
        const { status, since } = connectedAgents.find((a) => a.userId === agent.id) ?? {};
        return { ...agent, lastAgentStatus: status, createdAt: since };
      }),
    [connectedAgents, users],
  );

  const handleAnchorElement = (event: React.MouseEvent<HTMLSpanElement>) => {
    setSelectedUserId(event.currentTarget.dataset.id);
    setAnchorEl(event.currentTarget);
  };

  const handleRoleChange = (event: any) => {
    const {
      target: { value },
    } = event;
    setSelectedRoles(typeof value === 'string' ? value.split(',') : value);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedUserId(undefined);
  };

  const handleOnSearch = (value: string) => {
    setSearch(value);
  };

  return (
    <ListLayout title={t('users.list.title')} subtitle={t('users.list.subtitle')}>
      <Box display="flex" alignItems="center" gap={theme.spacing(3)} marginBottom={theme.spacing(3)}>
        <Typography
          variant="subtitle1"
          align="left"
          color={theme.palette.text.primary}
          sx={{
            flexGrow: 1,
          }}
        >
          {users?.count} {t('teams.results')}
        </Typography>

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="roles-label">{t('roles.title')}</InputLabel>
          <Select
            labelId="roles-label"
            multiple
            size="small"
            value={selectedRoles}
            onChange={handleRoleChange}
            displayEmpty
            renderValue={(selected) => selected.map((s) => t(`roles.${s}`)).join(', ')}
          >
            {rolesOptions.map((role) => (
              <MenuItem key={role} value={role}>
                <Checkbox checked={selectedRoles.indexOf(role) > -1} />
                <ListItemText primary={t(`roles.${role}`)} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <SearchInput placeholder="Buscar" onSearch={handleOnSearch} />
        <Can I="create" a="user" ability={ability}>
          <ToggleButtonGroup
            exclusive
            sx={{
              height: theme.spacing(5),
              '& button': {
                padding: theme.spacing(1),
                width: theme.spacing(5),
              },
            }}
          >
            <ToggleButton value="add" aria-label="right aligned" onClick={toggleShowCreateUser}>
              <Add />
            </ToggleButton>
          </ToggleButtonGroup>
          <CreateUserWithClient open={showCreateUser} onClose={toggleShowCreateUser} />
        </Can>
      </Box>

      {usersWithStatus?.length !== undefined && usersWithStatus.length > 0 && (
        <Table
          columns={[
            {
              id: 'name',
              label: t('users.table.name'),
              align: 'left',
            },
            {
              id: 'email',
              label: t('users.table.email'),
              align: 'left',
            },
            {
              id: 'agencyName',
              label: t('users.table.agency'),
              align: 'left',
            },
            {
              id: 'roles',
              label: t('users.table.roles'),
              align: 'center',
            },
            {
              id: 'client',
              label: t('users.table.client'),
              align: 'left',
            },
            { id: 'agentStatus', label: 'Estado', align: 'left' },
          ]}
          data={{
            key: 'email',
            items: usersWithStatus.map((item) => ({
              name: (
                <Link to={routes.dashboard.userView.replace(':id', item.id)}>
                  {`${item.firstName} ${item.lastName}`}
                </Link>
              ),
              email: item.email,
              agencyName: (
                <Link to={routes.dashboard.agencyView.root.replace(':id', String(item.agencyId))}>
                  {item.agencyName}
                </Link>
              ),
              roles: (
                <Box display="flex" flexDirection="column" gap={1} component="span">
                  {item.roles?.split(',').map((g, i) => (
                    <Typography variant="caption" key={i}>
                      {g}
                    </Typography>
                  ))}
                </Box>
              ),
              client: (
                <IconButton>
                  <PermContactCalendar color="primary" />
                </IconButton>
              ),
              agentStatus: item.lastAgentStatus && (
                <>
                  <Box
                    onClick={handleAnchorElement}
                    sx={{ cursor: 'pointer' }}
                    data-id={item.id}
                    display="flex"
                    alignItems="center"
                    gap={1}
                  >
                    <Box
                      width={16}
                      height={16}
                      borderRadius="50%"
                      sx={{
                        backgroundColor: getAgentStatusColor(item.lastAgentStatus as AgentStatus, theme),
                      }}
                    />
                    <Typography variant="subtitle2">{t(`agentStatus.${item.lastAgentStatus}`)}</Typography>
                    <Typography variant="caption">
                      <TimeAgo date={item.lastStatusDate} />
                    </Typography>
                  </Box>
                </>
              ),
            })),
            totalCount: users?.count || 0,
          }}
          pagination={pagination}
          stickyHeader
        />
      )}
      {selectedUserId && (
        <UserStatusMenu
          onClose={handleClose}
          anchorEl={anchorEl}
          userId={selectedUserId}
          queryKeys={[QueryKeys.getAllUsers, QueryKeys.getMyUserData]}
        />
      )}
      {isLoading && <Skeleton height={theme.spacing(10)} variant="rounded" animation="wave" />}
    </ListLayout>
  );
};
